import React, { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import { HelmetProvider } from 'react-helmet-async'
import { Notifications } from './components/Layout/Notifications'
// Removed unused ProtectedRoute and GuestRoute; loaders handle auth
import { createBrowserRouter, RouterProvider, redirect } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { AuthPage } from '@/components/Auth/AuthPage'
import { Landing } from '@/routes/Landing'
import { AppRouter } from '@/routes/AppRouter'
import { ProtectedApp } from '@/routes/ProtectedApp'
import { PrivacyPolicy } from '@/routes/PrivacyPolicy'
import { TermsOfService } from '@/routes/TermsOfService'
import { Pricing } from '@/routes/Pricing'
import { Contact } from '@/routes/Contact'
import { Homepage1 } from '@/routes/Homepage1'
import { Homepage2 } from '@/routes/Homepage2'
import { Homepage3 } from '@/routes/Homepage3'
import { HomepageFinal } from '@/routes/HomepageFinal'
import { initializeTheme } from './lib/theme'
// Removed Toaster; using global Notifications bottom-left
import { AcceptInvite } from '@/components/Auth/AcceptInvite'
import { ForgotPassword } from '@/components/Auth/ForgotPassword'
import { ResetPassword } from '@/components/Auth/ResetPassword'
import { apiFetch } from '@/lib/api'
import { AppErrorBoundary } from '@/components/Layout/AppErrorBoundary'
import { queryClient } from '@/lib/queryClient'
import { BlogListing } from '@/routes/blog/BlogListing'
import { BlogDetail } from '@/routes/blog/BlogDetail'
import { HelpHome, HelpCategory, HelpArticle, HelpSearch } from '@/routes/help'
import { CheckoutConfirm } from '@/routes/CheckoutConfirm'
import { RefundPolicy } from '@/routes/RefundPolicy'
import { CookiePolicy } from '@/routes/CookiePolicy'
import { VerifyEmail } from '@/routes/VerifyEmail'

// Dev-only: lazy-load Agentation feedback toolbar.
// import.meta.env.DEV is statically replaced by Vite at build time —
// in production the dynamic import is dead-code-eliminated, so the
// agentation package is never bundled.
const DevAgentation = import.meta.env.DEV
  ? lazy(() => import('agentation').then(m => ({ default: m.Agentation })))
  : null

function trackNavigation(_path: string) {}

// Initialize theme on app load
initializeTheme()

const router = createBrowserRouter([
  {
    path: '/',
    element: <Landing />,
  },
  {
    path: '/pricing',
    element: <Pricing />,
  },
  {
    path: '/contact',
    element: <Contact />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms',
    element: <TermsOfService />,
  },
  {
    path: '/refund-policy',
    element: <RefundPolicy />,
  },
  {
    path: '/cookie-policy',
    element: <CookiePolicy />,
  },
  {
    path: '/accept-invite',
    element: <AcceptInvite />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/homepage-1',
    element: <Homepage1 />,
  },
  {
    path: '/homepage-2',
    element: <Homepage2 />,
  },
  {
    path: '/homepage-3',
    element: <Homepage3 />,
  },
  {
    path: '/homepage-final',
    element: <HomepageFinal />,
  },
  {
    path: '/auth',
    loader: async () => {
      try {
        const res = await apiFetch('/api/session/bootstrap', { skipRedirect: true });
        if (!res.ok) return null; // not logged in — show auth page
        const payload = await res.json().catch(() => ({ data: {} }));
        const active = payload?.data?.activeOrgId;
        const activeOrg = payload?.data?.orgs?.find((org: any) => org.id === active);
        if (active && activeOrg && !activeOrg.onboarding_completed) throw redirect('/onboarding');
        throw redirect('/dashboard');
      } catch (e) {
        if (e instanceof Response) throw e; // re-throw redirects
        return null; // network error — show auth page
      }
    },
    element: <AuthPage />,
  },
  {
    path: '/blog',
    element: <BlogListing />,
  },
  {
    path: '/blog/:slug',
    element: <BlogDetail />,
  },
  {
    path: '/help',
    element: <HelpHome />,
  },
  {
    path: '/help/search',
    element: <HelpSearch />,
  },
  {
    path: '/help/:slug',
    element: <HelpCategory />,
  },
  {
    path: '/help/:category/:slug',
    element: <HelpArticle />,
  },
  {
    path: '/checkout/confirm',
    element: <CheckoutConfirm />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  {
    path: '/*',
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const pathname = url.pathname;
      trackNavigation(pathname);
      try {
        const res = await apiFetch('/api/session/bootstrap');
        if (!res.ok) throw redirect('/auth');
        const payload = await res.json().catch(() => ({ data: {} }));
        const active = payload?.data?.activeOrgId;
        const activeOrg = payload?.data?.orgs?.find((org: any) => org.id === active);
        if (active && activeOrg) {
          const completed = activeOrg.onboarding_completed === true;
          if (completed === false && pathname !== '/onboarding') throw redirect('/onboarding');
          if (completed === true && pathname === '/onboarding') throw redirect('/dashboard');
        }
        return payload?.data || null;
      } catch (e) {
        throw redirect('/auth');
      }
    },
    element: <ProtectedApp />,
  },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Notifications />
          {DevAgentation && <Suspense fallback={null}><DevAgentation /></Suspense>}
        </QueryClientProvider>
      </AppErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>,
)


